import "./styles.css";
import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import usePopupStore from "@/hooks/usePopupStore";
import useSearchFilters from "@/hooks/useSearchFilters";
import ActionsBar, { type ActionsBarExtraAction } from "@/UI/components/ActionsBar";
import { FilterBool, FilterSelect } from "@/UI/inputs/FilterSelect";
import FilterRange from "@/UI/inputs/FilterRange";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import SidePopup from "@/UI/popups/SidePopup";
import ProductsForm from "@/UI/forms/ProductsForm";
import CategoriesForm from "@/UI/forms/CategoriesForm";
import SortCategoriesPopup, { type SortCategoriesPopupDetails } from "@/UI/popups/SortCategoriesPopup";
import SortProductsPopup, { type SortProductsPopupDetails } from "@/UI/popups/SortProductsPopup";

export default function ProductsScreen() {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const { addComp, removeComp } = usePopupStore();
    const location = useLocation();

    const isCategories = location.pathname.includes("/categories");

    const tabs = [
        { label: t.nav.products, path: "/products", end: true },
        { label: t.nav.categories, path: "/products/categories" },
    ];

    // ── Fields fetch ────────────────────────────────────────────────────────────
    const { data: fieldsData } = useFetch("products/fields");
    const temperatureOptions: { title: string; key: string }[] = fieldsData?.data?.temperature ?? [];
    const categoryOptions: { _id: string; name: string }[] = fieldsData?.data?.categories ?? [];

    // ── Products filters ────────────────────────────────────────────────────────
    const productFilters = useSearchFilters({
        search: undefined as string | undefined,
        category: [] as string[],
        temperature: [] as string[],
        active: [] as string[],
        hidden: [] as string[],
        min_price: undefined as string | undefined,
        max_price: undefined as string | undefined,
    });

    // ── Categories filters ──────────────────────────────────────────────────────
    const categoryFilters = useSearchFilters({
        search: undefined as string | undefined,
    });

    const filters = isCategories ? categoryFilters : productFilters;

    // Clear filter mode when switching tabs
    useEffect(() => {
        if (productFilters.filterOpen) productFilters.cancelFilter();
        if (categoryFilters.filterOpen) categoryFilters.cancelFilter();
    }, [isCategories]);

    // ── Add forms ───────────────────────────────────────────────────────────────
    const openAddProductForm = () => {
        const name = "add-product";
        removeComp(name);
        addComp({
            name,
            Component: SidePopup,
            details: {
                title: t.productForm.createTitle,
                Content: ProductsForm,
                contentDetails: {
                    onSuccess: () => {
                        window.dispatchEvent(new CustomEvent("products:refresh"));
                    },
                },
            },
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            coverClose: true,
        });
    };

    const openAddCategoryForm = () => {
        const name = "add-category";
        removeComp(name);
        addComp({
            name,
            Component: SidePopup,
            details: {
                title: t.categoryForm.createTitle,
                Content: CategoriesForm,
                contentDetails: {
                    onSuccess: () => {
                        window.dispatchEvent(new CustomEvent("categories:refresh"));
                    },
                },
            },
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Sort popups ─────────────────────────────────────────────────────────────
    const openSortProductsPopup = () => {
        const name = "sort-products";
        removeComp(name);
        addComp({
            name,
            Component: SortProductsPopup,
            details: { onSuccess: () => window.dispatchEvent(new CustomEvent("products:refresh")) } as SortProductsPopupDetails,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    const openSortCategoriesPopup = () => {
        const name = "sort-categories";
        removeComp(name);
        addComp({
            name,
            Component: SortCategoriesPopup,
            details: { onSuccess: () => window.dispatchEvent(new CustomEvent("categories:refresh")) } as SortCategoriesPopupDetails,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    const handleAdd = isCategories ? openAddCategoryForm : openAddProductForm;

    const sortIcon = (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M2 4.5h14M2 9h10M2 13.5h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    );

    const extraActions: ActionsBarExtraAction[] = isCategories
        ? [{ name: "sort-categories", handler: openSortCategoriesPopup, children: sortIcon }]
        : [{ name: "sort-products", handler: openSortProductsPopup, children: sortIcon }];

    // ── Filter content ──────────────────────────────────────────────────────────
    const productFilterContent = (
        <>
            <FilterSearchInput
                value={productFilters.draft.search}
                onChange={(v) => productFilters.setDraftField("search", v)}
                placeholder={t.filters.products.search}
            />
            {categoryOptions.length > 0 && (
                <FilterSelect
                    label={t.filters.products.category}
                    options={categoryOptions.map((o) => ({ value: o._id, label: o.name }))}
                    value={productFilters.draft.category as string[]}
                    onChange={(v) => productFilters.setDraftField("category", v)}
                />
            )}
            {temperatureOptions.length > 0 && (
                <FilterSelect
                    label={t.filters.products.temperature}
                    options={temperatureOptions.map((o) => ({ value: o.key, label: o.title }))}
                    value={productFilters.draft.temperature as string[]}
                    onChange={(v) => productFilters.setDraftField("temperature", v)}
                />
            )}
            <FilterBool
                label={t.filters.products.active}
                value={productFilters.draft.active as string[]}
                onChange={(v) => productFilters.setDraftField("active", v)}
                yesLabel={t.filters.yes}
                noLabel={t.filters.no}
            />
            <FilterBool
                label={t.filters.products.hidden}
                value={productFilters.draft.hidden as string[]}
                onChange={(v) => productFilters.setDraftField("hidden", v)}
                yesLabel={t.filters.yes}
                noLabel={t.filters.no}
            />
            <FilterRange
                label={t.filters.products.priceRange}
                fromValue={productFilters.draft.min_price}
                toValue={productFilters.draft.max_price}
                onFromChange={(v) => productFilters.setDraftField("min_price", v)}
                onToChange={(v) => productFilters.setDraftField("max_price", v)}
                fromPlaceholder={t.filters.from}
                toPlaceholder={t.filters.to}
            />
        </>
    );

    const categoryFilterContent = (
        <FilterSearchInput
            value={categoryFilters.draft.search}
            onChange={(v) => categoryFilters.setDraftField("search", v)}
            placeholder={t.filters.categories.search}
        />
    );

    return (
        <div className="products-screen">
            <ActionsBar
                tabs={tabs}
                onAdd={handleAdd}
                extraActions={extraActions}
                filterContent={isCategories ? categoryFilterContent : productFilterContent}
                filterOpen={filters.filterOpen}
                onFilterOpen={filters.openFilter}
                onFilterSave={() =>
                    filters.saveFilter((q) => {
                        window.dispatchEvent(new CustomEvent(isCategories ? "categories:filter" : "products:filter", { detail: q }));
                    })
                }
                onFilterClear={() =>
                    filters.clearFilter(() => {
                        window.dispatchEvent(new CustomEvent(isCategories ? "categories:filter" : "products:filter", { detail: {} }));
                    })
                }
                onFilterCancel={filters.cancelFilter}
                hasActiveFilters={filters.hasActiveFilters}
            />
            <div className="products-screen-content">
                <Outlet />
            </div>
        </div>
    );
}
